import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import {
  Actions,
  BackIcon,
  BrandHeaderRow,
  BrandLogo,
  Button,
  Header,
  HeaderIconButton,
  HeaderText,
  Message,
  Muted,
  Page,
  PlusIcon,
  Shell,
  Subtitle,
  Title
} from '../components/PageTheme';
import { getStreetAddressFromGeocodeResult } from '../utils/addressLabels';
import { formatLocationRadius } from '../utils/distanceUnits';
import useBackNavigation from '../utils/useBackNavigation';

const HeaderActions = styled(Actions)`
  align-self: flex-start;
  margin-left: auto;
`;

const LocationsList = styled.div`
  display: grid;
  gap: 0.85rem;
  margin-top: 1rem;
`;

const LocationItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 1rem;
  border: 1px solid rgba(226, 232, 240, 0.1);
  border-radius: 24px;
  background: rgba(255, 255, 255, 0.045);

  @media (max-width: 640px) {
    align-items: flex-start;
    flex-direction: column;
  }
`;

const LocationSummary = styled.div`
  display: flex;
  align-items: center;
  gap: 0.9rem;
  min-width: 0;

  @media (max-width: 640px) {
    width: 100%;
  }
`;

const StaticMapLink = styled.a`
  display: inline-flex;
  flex: 0 0 auto;
  border-radius: 16px;
`;

const StaticMap = styled.img`
  width: 116px;
  height: 82px;
  flex: 0 0 auto;
  border: 1px solid rgba(226, 232, 240, 0.12);
  border-radius: 16px;
  object-fit: cover;
  background: rgba(15, 23, 42, 0.72);
`;

const LocationName = styled.div`
  color: #f8fafc;
  font-size: 1.15rem;
  font-weight: 950;
`;

const LocationDetails = styled.div`
  display: grid;
  gap: 0.2rem;
`;

const LocationActions = styled(Actions)`
  justify-content: flex-end;
`;

const LockedActionGroup = styled.div`
  display: grid;
  justify-items: end;
  gap: 0.35rem;
`;

const ButtonRow = styled(Actions)`
  justify-content: flex-end;
`;

const LockedReason = styled(Muted)`
  max-width: 16rem;
  font-size: 0.78rem;
  font-weight: 800;
  line-height: 1.25;
  text-align: right;

  @media (max-width: 640px) {
    max-width: none;
    text-align: left;
  }
`;

const getLocationPosition = (location) => {
  if (location.location) {
    return location.location;
  }

  return {
    lat: location.lat,
    lng: location.lng
  };
};

const getStaticMapUrl = (location, apiKey) => {
  const position = getLocationPosition(location);

  if (!apiKey || position?.lat === undefined || position?.lng === undefined) {
    return null;
  }

  const marker = encodeURIComponent(`color:orange|${position.lat},${position.lng}`);

  return `https://maps.googleapis.com/maps/api/staticmap?center=${position.lat},${position.lng}&zoom=16&size=232x164&scale=2&maptype=roadmap&markers=${marker}&key=${apiKey}`;
};

const getMapsUrl = (location) => {
  const position = getLocationPosition(location);

  if (position?.lat === undefined || position?.lng === undefined) {
    return null;
  }

  const query = encodeURIComponent(`${position.lat},${position.lng}`);

  return `https://maps.apple.com/?ll=${query}&q=${encodeURIComponent(location.name || query)}`;
};

const isUserSavedLocation = (location) => {
  return location?.temporary !== true && location?.source !== 'run_pledge';
};

const ManageLocations = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const goBack = useBackNavigation('/dashboard');
  const [locations, setLocations] = useState([]);
  const [activePledges, setActivePledges] = useState([]);
  const [locationAddresses, setLocationAddresses] = useState({});
  const [confirmingDeleteId, setConfirmingDeleteId] = useState('');
  const [deletingLocationId, setDeletingLocationId] = useState('');
  const [error, setError] = useState('');
  const googleMapsApiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

  const lockedLocationIds = useMemo(() => {
    return new Set(activePledges.map(pledge => pledge.locationId).filter(Boolean));
  }, [activePledges]);

  const fetchLocations = useCallback(async () => {
    try {
      const idToken = await user.getIdToken();
      const [locationsResponse, pledgesResponse] = await Promise.all([
        axios.get(`${window.RUNTIME_CONFIG.apiUrl}/locations`, {
          headers: { Authorization: `Bearer ${idToken}` }
        }),
        axios.get(`${window.RUNTIME_CONFIG.apiUrl}/alarms`, {
          params: { active: true },
          headers: { Authorization: `Bearer ${idToken}` }
        })
      ]);

      setLocations(locationsResponse.data.filter(isUserSavedLocation));
      setActivePledges(pledgesResponse.data.items || pledgesResponse.data);
    } catch (error) {
      console.error('Error fetching locations:', error);
      setError('Failed to fetch locations');
    }
  }, [user]);

  useEffect(() => {
    fetchLocations();
  }, [fetchLocations]);

  useEffect(() => {
    if (!googleMapsApiKey || locations.length === 0) {
      return;
    }

    const fetchAddresses = async () => {
      const missingLocations = locations.filter(location => !Object.prototype.hasOwnProperty.call(locationAddresses, location.id));

      if (missingLocations.length === 0) {
        return;
      }

      const addressEntries = await Promise.all(missingLocations.map(async (location) => {
        const position = getLocationPosition(location);

        if (position?.lat === undefined || position?.lng === undefined) {
          return [location.id, null];
        }

        try {
          const response = await fetch(
            `https://maps.googleapis.com/maps/api/geocode/json?latlng=${position.lat},${position.lng}&key=${googleMapsApiKey}`
          );
          const data = await response.json();
          return [location.id, getStreetAddressFromGeocodeResult(data.results?.[0])];
        } catch (error) {
          console.error('Error fetching location address:', error);
          return [location.id, null];
        }
      }));

      setLocationAddresses(prev => ({
        ...prev,
        ...Object.fromEntries(addressEntries)
      }));
    };

    fetchAddresses();
  }, [googleMapsApiKey, locationAddresses, locations]);

  const handleDeleteLocation = async (locationId) => {
    if (confirmingDeleteId !== locationId) {
      setConfirmingDeleteId(locationId);
      return;
    }

    setDeletingLocationId(locationId);
    setError('');

    try {
      await axios.delete(
        `${window.RUNTIME_CONFIG.apiUrl}/locations/${locationId}`,
        {
          headers: { Authorization: `Bearer ${await user.getIdToken()}` }
        }
      );
      setConfirmingDeleteId('');
      await fetchLocations();
    } catch (error) {
      console.error('Error deleting location:', error);
      setError(error.response?.data?.message || 'Failed to delete location');
    } finally {
      setDeletingLocationId('');
    }
  };

  return (
    <Page>
      <Shell $wide>
        <Header>
          <HeaderText>
            <BrandHeaderRow>
              <BrandLogo />
              <HeaderActions>
                <HeaderIconButton type="button" aria-label="Back" onClick={goBack}>
                  <BackIcon aria-hidden="true" />
                </HeaderIconButton>
                <HeaderIconButton type="button" aria-label="New location" onClick={() => navigate('/manage-locations/new')}>
                  <PlusIcon aria-hidden="true" />
                </HeaderIconButton>
              </HeaderActions>
            </BrandHeaderRow>
            <Title>Locations.</Title>
            <Subtitle>Your saved destinations for future pledges.</Subtitle>
          </HeaderText>
        </Header>

        {error && <Message $error>{error}</Message>}

        <LocationsList>
          {locations.map((location, index) => {
            const staticMapUrl = getStaticMapUrl(location, googleMapsApiKey);
            const mapsUrl = getMapsUrl(location);
            const lockedByActivePledge = lockedLocationIds.has(location.id);

            return (
              <LocationItem key={location.id || index}>
                <LocationSummary>
                  {staticMapUrl && mapsUrl && (
                    <StaticMapLink href={mapsUrl} target="_blank" rel="noreferrer" aria-label={`Open ${location.name} in maps`}>
                      <StaticMap src={staticMapUrl} alt="" loading="lazy" />
                    </StaticMapLink>
                  )}
                  <LocationDetails>
                    <LocationName>{location.name}</LocationName>
                    {locationAddresses[location.id] && <Muted>{locationAddresses[location.id]}</Muted>}
                    <Muted>{formatLocationRadius(location)}</Muted>
                  </LocationDetails>
                </LocationSummary>
                <LocationActions>
                  <LockedActionGroup>
                    <ButtonRow>
                      <Button
                        $secondary
                        type="button"
                        disabled={lockedByActivePledge}
                        onClick={() => navigate(`/manage-locations/${location.id}/edit`)}
                      >
                        Edit
                      </Button>
                      <Button
                        $danger
                        type="button"
                        disabled={lockedByActivePledge || deletingLocationId === location.id}
                        onClick={() => handleDeleteLocation(location.id)}
                      >
                        {deletingLocationId === location.id
                          ? 'Deleting...'
                          : confirmingDeleteId === location.id
                            ? 'Confirm delete'
                            : 'Delete'}
                      </Button>
                      {confirmingDeleteId === location.id && (
                        <Button $secondary type="button" onClick={() => setConfirmingDeleteId('')}>
                          Cancel
                        </Button>
                      )}
                    </ButtonRow>
                    {lockedByActivePledge && (
                      <LockedReason>Locked while an active pledge uses this location.</LockedReason>
                    )}
                  </LockedActionGroup>
                </LocationActions>
              </LocationItem>
            );
          })}
        </LocationsList>
      </Shell>
    </Page>
  );
};

export default ManageLocations;
